/**
 * MoonPhaseEntry — MP1 screen of the Moon Phase Soulmate flow.
 *
 * Route: /flow/moon-phase-soulmate
 *
 * Layout:
 *   - CosmosBackdrop (decorative)
 *   - Back button
 *   - Eyebrow badge: "RITE · FREE"
 *   - LivingSigil size=180 state="breathing" archetype="moon_phase_soulmate"
 *   - Triadic title (3 lines, middle italic)
 *   - Short blurb (2–3 sentences)
 *   - Explainer cards
 *   - Free note
 *   - Primary CTA "Begin" → /flow/moon-phase-soulmate/computing
 *   - Secondary "Not now" → navigate(-1)
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../../brand/CosmosBackdrop';
import { LivingSigil } from '../../brand/LivingSigil';
import { AppHeader } from '../../chrome/AppHeader';
import { PrimaryButton, SecondaryButton } from '../../chrome/Button';
import styles from './MoonPhase.module.css';

// --------------------------------------------------------------------------
// Main screen
// --------------------------------------------------------------------------

export function MoonPhaseEntry(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation('flows');

  function handleBegin() {
    void navigate('/flow/moon-phase-soulmate/computing');
  }

  function handleNotNow() {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      void navigate('/catalog');
    }
  }

  return (
    <div className={styles.root}>
      <CosmosBackdrop />

      <main
        role="main"
        aria-labelledby="moonphase-heading"
        className={styles.content}
      >
        {/* Header */}
        <AppHeader
          title={t('moonPhase.headerTitle')}
          onBack={() => { if (window.history.length > 1) navigate(-1); else void navigate('/catalog'); }}
          backTestId="mp-back-btn"
          headingId="moonphase-heading"
        />

        {/* Eyebrow badge */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span className={styles.eyebrowBadge} data-testid="mp-entry-badge">
            ● {t('moonPhase.entry.badge')}
          </span>
        </div>

        {/* Hero sigil */}
        <div className={styles.sigilWrap} data-testid="mp-entry-sigil">
          <LivingSigil size={180} state="breathing" archetype="moon_phase_soulmate" />
        </div>

        {/* Triadic title — 3 lines, middle italic */}
        <div style={{ textAlign: 'center' }}>
          <h1 className={styles.triadicTitle} data-testid="mp-entry-title">
            <span className={styles.triadicLine}>{t('moonPhase.entry.titleLine1')}</span>
            <em className={styles.triadicLineItalic}>{t('moonPhase.entry.titleLine2')}</em>
            <span className={styles.triadicLine}>{t('moonPhase.entry.titleLine3')}</span>
          </h1>
        </div>

        {/* Blurb */}
        <p className={styles.blurb} data-testid="mp-entry-blurb">
          {t('moonPhase.entry.blurb')}
        </p>

        {/* Explainer cards */}
        <div className={styles.explainerCard}>
          {[
            {
              icon: '🌕',
              title: t('moonPhase.entry.card1Title'),
              sub: t('moonPhase.entry.card1Sub'),
            },
            {
              icon: '⚭',
              title: t('moonPhase.entry.card2Title'),
              sub: t('moonPhase.entry.card2Sub'),
            },
            {
              icon: '↗',
              title: t('moonPhase.entry.card3Title'),
              sub: t('moonPhase.entry.card3Sub'),
            },
          ].map((row) => (
            <div key={row.icon} className={styles.explainerRow}>
              <span className={styles.explainerIcon} aria-hidden="true">
                {row.icon}
              </span>
              <div>
                <p className={styles.explainerTitle}>{row.title}</p>
                <p className={styles.explainerSub}>{row.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Free note */}
        <div className={styles.freeNote}>
          <span className={styles.freeNoteStar} aria-hidden="true">✦</span>
          <span>{t('moonPhase.entry.freeNote')}</span>
        </div>

        {/* CTA area */}
        <div className={styles.ctaArea}>
          <PrimaryButton
            onClick={handleBegin}
            data-testid="mp-entry-begin-btn"
          >
            {t('moonPhase.entry.begin')}
          </PrimaryButton>
          <SecondaryButton
            onClick={handleNotNow}
            data-testid="mp-entry-notnow-btn"
          >
            {t('moonPhase.entry.notNow')}
          </SecondaryButton>
        </div>
      </main>
    </div>
  );
}
